import React from "react";

/**
 * Inline-SVG sparkline showing per-stage weekly avg-days-to-advance.
 *
 * Uses lightweight native SVG (no recharts dep) because these render in
 * every Kanban column and the visual footprint is ~120×26 px. Rising
 * trends (latest > first × 1.05) auto-colour orange with a ↑ hint so
 * bottlenecks surface at a glance. When `onClick` is provided the whole
 * pill becomes keyboard-focusable and clickable (used to open the
 * TrendHistoryModal drill-down).
 */
export default function StageTrendSparkline({ trend, testid, onClick }) {
  const series = trend || [];
  const values = series.map((t) => (t?.avg_days == null ? null : Number(t.avg_days)));
  const nonNull = values.filter((v) => v != null);

  const clickProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
        },
      }
    : {};
  const clickClasses = onClick
    ? "cursor-pointer hover:opacity-75 focus:outline-none focus:ring-1 focus:ring-[var(--accent-hover)] rounded-sm"
    : "";

  if (nonNull.length < 2) {
    return (
      <div
        className={`text-[9px] muted font-mono italic ${clickClasses}`}
        data-testid={testid}
        title={
          onClick
            ? "Not enough weekly data yet — click to open the trend history"
            : "Not enough completed transitions yet to draw a weekly trend"
        }
        {...clickProps}
      >
        {nonNull.length === 1 ? "1 wk data" : "no trend yet"}
      </div>
    );
  }

  const W = 120, H = 26, PAD = 2;
  const max = Math.max(...nonNull);
  const min = Math.min(...nonNull);
  const span = Math.max(0.5, max - min);
  const stepX = (W - PAD * 2) / (values.length - 1);

  const points = values.map((v, i) => {
    if (v == null) return null;
    return [PAD + i * stepX, H - PAD - ((v - min) / span) * (H - PAD * 2)];
  });

  let path = "";
  let areaPath = "";
  let lastPoint = null;
  for (const p of points) {
    if (!p) continue;
    if (!path) {
      path = `M ${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
      areaPath = `M ${p[0].toFixed(1)} ${(H - PAD).toFixed(1)} L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
    } else {
      path += ` L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
      areaPath += ` L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
    }
    lastPoint = p;
  }
  if (lastPoint) areaPath += ` L ${lastPoint[0].toFixed(1)} ${(H - PAD).toFixed(1)} Z`;

  const firstNonNull = nonNull[0];
  const lastNonNull = nonNull[nonNull.length - 1];
  const rising = lastNonNull > firstNonNull * 1.05;
  const color = rising ? "#F59E0B" : "var(--accent-hover)";
  const baseLabel = rising
    ? `Trend rising — ${firstNonNull.toFixed(1)}d → ${lastNonNull.toFixed(1)}d over ${values.length} weeks`
    : `Weekly avg to advance — latest ${lastNonNull.toFixed(1)}d over ${nonNull.length} data points`;
  const label = onClick ? `${baseLabel}. Click to open the full trend history.` : baseLabel;

  return (
    <div
      className={`inline-flex items-center gap-1.5 ${clickClasses}`}
      data-testid={testid}
      title={label}
      {...clickProps}
    >
      <svg width={W} height={H} className="shrink-0" aria-hidden>
        <path d={areaPath} fill={color} opacity="0.12" />
        <path d={path} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
        {lastPoint && <circle cx={lastPoint[0]} cy={lastPoint[1]} r="2" fill={color} />}
      </svg>
      {rising && (
        <span
          className="text-[9px] font-bold"
          style={{ color }}
          data-testid={testid ? `${testid}-rising` : undefined}
        >
          ↑
        </span>
      )}
    </div>
  );
}
